import type { ConfigFieldType } from '~/settings/components/ConfigurationsPage/types';

export interface ParsedValue {
  value: number;
  unit: string;
  displayValue: string;
}

export const sizeUnitMap: Record<string, string> = {
  kb: 'KiB',
  mb: 'MiB',
  gb: 'GiB',
  tb: 'TiB',
};

export const sizeApiUnitMap: Record<string, string> = {
  KiB: 'kb',
  MiB: 'mb',
  GiB: 'gb',
  TiB: 'tb',
};

export const durationUnitMap: Record<string, string> = {
  s: 's',
  m: 'm',
  h: 'h',
};

export const durationApiUnitMap: Record<string, string> = {
  seconds: 's',
  minutes: 'm',
  hours: 'h',
};

/**
 * Parse a configuration value string based on its type
 * Examples:
 * - "5gb" -> { value: 5, unit: "GiB", displayValue: "5" }
 * - "2s" -> { value: 2, unit: "s", displayValue: "2" }
 * - "90" (percentage) -> { value: 90, unit: "%", displayValue: "90" }
 * - "1.5" (positive_float) -> { value: 1.5, unit: "", displayValue: "1.5" }
 */
export function parseConfigValue(rawValue: string, type: ConfigFieldType): ParsedValue {
  const trimmedValue = rawValue.trim();

  switch (type) {
    case 'size':
      return parseSizeValue(trimmedValue);
    case 'duration':
      return parseDurationValue(trimmedValue);
    case 'percentage':
      return parsePercentageValue(trimmedValue);
    case 'positive_float':
    case 'string':
      return {
        value: parseFloat(trimmedValue) || 0,
        unit: '',
        displayValue: trimmedValue,
      };
    case 'boolean':
      return {
        value: trimmedValue.toLowerCase() === 'true' ? 1 : 0,
        unit: '',
        displayValue: trimmedValue,
      };
    case 'enum':
      return {
        value: 0,
        unit: '',
        displayValue: trimmedValue,
      };
    default:
      return {
        value: 0,
        unit: '',
        displayValue: trimmedValue,
      };
  }
}

/**
 * Parse size values like "5gb", "100mb", "1tb"
 * Normalizes to standard units (Bytes, KiB, MiB, GiB, TiB)
 */
function parseSizeValue(value: string): ParsedValue {
  const sizeRegex = /^(\d+(?:\.\d+)?)\s*([a-z]+)?$/i;
  const match = value.match(sizeRegex);

  if (!match) {
    return { value: 0, unit: 'GiB', displayValue: '0' };
  }

  const numValue = parseFloat(match[1]);
  const unit = (match[2] || 'bytes').toLowerCase();

  const displayUnit = sizeUnitMap[unit] || 'GiB';

  return {
    value: numValue,
    unit: displayUnit,
    displayValue: numValue.toString(),
  };
}

/**
 * Parse duration values like "2s", "5m", "1h", "100ms"
 * Supports: ms (milliseconds), s (seconds), m (minutes), h (hours)
 */
function parseDurationValue(value: string): ParsedValue {
  const durationRegex = /^(\d+(?:\.\d+)?)\s*([a-z]+)?$/i;
  const match = value.match(durationRegex);

  if (!match) {
    return { value: 0, unit: 's', displayValue: '0' };
  }

  const numValue = parseFloat(match[1]);
  const unit = (match[2] || 's').toLowerCase();

  const displayUnit = durationUnitMap[unit] || 's';

  return {
    value: numValue,
    unit: displayUnit,
    displayValue: numValue.toString(),
  };
}

/**
 * Parse percentage values - expects just a number
 * Example: "90" -> { value: 90, unit: "%", displayValue: "90" }
 */
function parsePercentageValue(value: string): ParsedValue {
  // Remove % if present
  const cleanValue = value.replace('%', '').trim();
  const numValue = parseFloat(cleanValue);

  return {
    value: isNaN(numValue) ? 0 : numValue,
    unit: '%',
    displayValue: isNaN(numValue) ? '0' : numValue.toString(),
  };
}

/**
 * Format a parsed value back to API format
 * Examples:
 * - { value: 5, unit: "GiB" } -> "5gb"
 * - { value: 2, unit: "s" } -> "2s"
 * - { value: 90, unit: "%" } -> "90"
 */
export function formatConfigValue(
  value: number | string,
  unit: string,
  type: ConfigFieldType
): string {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  switch (type) {
    case 'size':
      return formatSizeValue(numValue, unit);
    case 'duration':
      return formatDurationValue(numValue, unit);
    case 'percentage':
      return numValue.toString();
    case 'positive_float':
      return numValue.toString();
    case 'boolean':
      return value.toString();
    case 'enum':
      return value.toString();
    default:
      return value.toString();
  }
}

/**
 * Format size value back to API format
 * Examples: 5 GiB -> "5gb", 100 MiB -> "100mb"
 */
function formatSizeValue(value: number, unit: string): string {
  const apiUnit = sizeApiUnitMap[unit] || 'gb';
  return `${value}${apiUnit}`;
}

/**
 * Format duration value back to API format
 * Examples: 2 s -> "2s", 5 minutes -> "5m"
 */
function formatDurationValue(value: number, unit: string): string {
  const apiUnit = durationUnitMap[unit] || 's';
  return `${value}${apiUnit}`;
}

/**
 * Get available units for a given type
 */
export function getUnitsForType(type: ConfigFieldType): string[] {
  switch (type) {
    case 'size':
      return ['KiB', 'MiB', 'GiB', 'TiB'];
    case 'duration':
      return ['seconds', 'minutes', 'hours'];
    case 'percentage':
      return ['%'];
    default:
      return [];
  }
}
